/* eslint-disable eqeqeq */
import { Step } from '@dealstack/frontend/shared/components/SubstepMenu/step.types';
import { isNotEmpty } from '@dealstack/shared/lib/isNotEmpty.ts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { resolvePath, useLocation, useNavigate } from 'react-router-dom';

interface UseSubstepsOptions {
	initialSteps: Step[];
	initialStepId?: string | number;
	disabled?: boolean;
	/**
	 * Function called when we are trying to exit from current step.
	 *
	 * This function can be async and block execution which will delay
	 * setting the next step.
	 *
	 * If this function will return false then step change will no be performed.
	 */
	onWillLeaveStep?: (
		currentStepId: string | number,
		nextStepId?: string | number
	) => Promise<boolean>;
	onStepChange?: (id: string | number) => void;
	disableNext?: boolean;
	disablePrev?: boolean;
	disableGoTo?: boolean;
	useRoutesLogic?: boolean;
	/** Absolute path that relative `routeToNavigate` values resolve against. */
	routesBasePath?: string;
}

interface UseSubsteps {
	steps: Step[];
	currentStep: Step | null;
	nextStep: Step | null;
	goNext: () => Promise<void>;
	goPrev: () => Promise<void>;
	goToStep: (id: string | number) => Promise<void>;
	setSteps: (steps: Step[]) => void;
	changeStepTo: (id: string | number) => Promise<void>;
	resetSubstepsState: (newSteps: Step[], activeStepId?: string | number) => void;
	disabled?: boolean;
	isFirstStep: boolean;
	isLastStep: boolean;
}

export const useSubstepMenu = (options: UseSubstepsOptions): UseSubsteps => {
	const {
		initialSteps,
		initialStepId,
		onWillLeaveStep,
		onStepChange,
		disableNext,
		disablePrev,
		disableGoTo,
		useRoutesLogic,
		routesBasePath,
	} = options;

	const location = useLocation();
	const navigate = useNavigate();

	const [internalDisabled, setInternalDisabled] = useState(options?.disabled ?? false);
	const disabled = internalDisabled || options.disabled;

	const [steps, setSteps] = useState<Step[]>(initialSteps);
	const [currentStepId, setCurrentStepId] = useState<string | number>(() => {
		if (initialStepId != undefined) {
			return initialStepId;
		} else if (steps.length) {
			return steps[0].id;
		} else {
			return '';
		}
	});

	useEffect(() => {
		if (!useRoutesLogic) {
			return;
		}
		const activeSubStepByRoute = steps.find((step) =>
			step.routeToMatch ? location.pathname.includes(step.routeToMatch) : null
		);

		if (isNotEmpty(activeSubStepByRoute) && activeSubStepByRoute.id !== currentStepId) {
			void changeStepTo(activeSubStepByRoute.id);
		}
	}, [location, steps]);

	const changeStepTo = useCallback(
		async (nextStepId?: string | number) => {
			if (disabled) {
				return;
			}

			const idxCurrent = steps.findIndex((step: Step) => step.id === currentStepId);
			const idxNext = steps.findIndex((step: Step) => step.id === nextStepId);

			if (idxCurrent === -1) {
				return;
			}

			let shouldChangeStep = true;

			if (onWillLeaveStep) {
				setInternalDisabled(true);

				try {
					shouldChangeStep = await onWillLeaveStep(currentStepId, nextStepId);
				} finally {
					setInternalDisabled(false);
				}
			}

			if (shouldChangeStep) {
				if (nextStepId != undefined) {
					setCurrentStepId(nextStepId);

					if (useRoutesLogic) {
						const nextStepRoute = steps.find(
							(step: Step) => step.id === nextStepId
						)?.routeToNavigate;

						if (nextStepRoute) {
							navigate(
								routesBasePath
									? resolvePath(nextStepRoute, routesBasePath)
									: nextStepRoute
							);
						}
					}
				}

				setSteps(
					steps.map((step: Step, stepIndex: number) => {
						if (nextStepId ? stepIndex < idxNext : stepIndex === idxCurrent) {
							return { ...step, status: 'success' };
						} else {
							return step;
						}
					})
				);

				if (nextStepId != undefined) {
					onStepChange?.(nextStepId);
				}
			}
		},
		[disabled, currentStepId, onWillLeaveStep, steps, routesBasePath]
	);

	const changeStep = useCallback(
		async (direction: number) => {
			const idx = steps.findIndex((step: Step) => step.id === currentStepId);
			const nextStepId: string | number | undefined = steps[idx + direction]?.id;

			return changeStepTo(nextStepId);
		},
		[changeStepTo, currentStepId, steps]
	);

	const goToStep = useCallback(
		async (nextStepId?: string | number) => {
			if (disableGoTo) {
				return;
			}

			await changeStepTo(nextStepId);
		},
		[disableGoTo, changeStepTo]
	);

	const goNext = useCallback(async () => {
		if (disableNext) {
			return;
		}

		await changeStep(1);
	}, [disableNext, changeStep]);

	const goPrev = useCallback(async () => {
		if (disablePrev) {
			return;
		}

		await changeStep(-1);
	}, [disablePrev, changeStep]);

	const currentStep = useMemo(
		() => steps.find((step: Step) => step.id === currentStepId) || null,
		[steps, currentStepId]
	);

	const nextStep = useMemo(() => {
		const currentIdx = steps.findIndex((step: Step) => step.id === currentStepId);
		const nextStep: Step | undefined = steps[currentIdx + 1];
		return nextStep;
	}, [steps, currentStepId]);

	const isFirstStep = useMemo(
		() => steps.findIndex((step) => step.id === currentStepId) === 0,
		[steps, currentStepId]
	);

	const isLastStep = useMemo(
		() => steps.findIndex((step) => step.id === currentStepId) === steps.length - 1,
		[steps, currentStepId]
	);

	const resetSubstepsState = useCallback((newSteps: Step[], activeStepId?: string) => {
		setSteps(newSteps);

		setCurrentStepId(activeStepId ?? newSteps[0]?.id ?? '');
	}, []);

	return {
		steps,
		currentStep,
		nextStep,
		changeStepTo,
		goNext,
		goPrev,
		goToStep,
		disabled,
		isFirstStep,
		isLastStep,
		resetSubstepsState,
		setSteps,
	};
};
